/**
 * Owner: Masa Bayu
 * File: reading-continuity.ts
 * Date:Time: 2026-09-18 13:57
 * Description: READER-PROD R3 — QXP page position refs · resume locus helpers.
 */

export const QXP_PAGE_REF_PREFIX = 'qxp_page_' as const;

export function encodeQxpPageRef(pageIndex: number): string {
  const n = Math.max(1, Math.floor(pageIndex));
  return `${QXP_PAGE_REF_PREFIX}${String(n)}`;
}

export function parseQxpPageRef(ref: string | null | undefined): number | null {
  if (typeof ref !== 'string' || !ref.startsWith(QXP_PAGE_REF_PREFIX)) {
    return null;
  }
  const raw = ref.slice(QXP_PAGE_REF_PREFIX.length);
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) {
    return null;
  }
  return n;
}

/**
 * Prefer explicit qxp_page_N locus; else percent → page within pageCount.
 */
export function resumePageIndexFromProgress(input: {
  readonly lastPositionRef: string | null;
  readonly percentComplete: number;
  readonly pageCount: number;
}): number {
  const pageCount = Math.max(0, Math.floor(input.pageCount));
  if (pageCount < 1) {
    return 1;
  }
  const fromRef = parseQxpPageRef(input.lastPositionRef);
  if (fromRef !== null) {
    return Math.max(1, Math.min(pageCount, fromRef));
  }
  const percent = input.percentComplete;
  if (typeof percent !== 'number' || !Number.isFinite(percent) || percent <= 0) {
    return 1;
  }
  return Math.max(1, Math.min(pageCount, Math.round((percent / 100) * pageCount)));
}

export function percentFromPageIndex(pageIndex: number, pageCount: number): number {
  if (pageCount < 1) {
    return 0;
  }
  const clamped = Math.max(1, Math.min(pageCount, Math.floor(pageIndex)));
  return Math.min(100, Math.max(1, Math.round((clamped / pageCount) * 100)));
}
